import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../../../environments/environment';
import { ApiMethodService } from '../../../../tools/services/api/method-api/apiMethod.service';
import { ActionBackOfficeResponse } from '../../../models/api.model';
import {
  DeactivateInvoiceFeePayload,
  InvoiceFee,
  InvoiceFeePayload,
  InvoiceSiteConfig,
  InvoiceSiteConfigPayload,
} from '../../../models/invoice-settings/invoice-settings.model';

@Injectable({ providedIn: 'root' })
export class ApiInvoiceSettingsService {
  private readonly baseUrl = environment.apiPrivateUrl;
  private readonly cookieService = inject(CookieService);
  private readonly apiMethodService = inject(ApiMethodService);

  getInvoiceFeeList(siteId?: number | null, activeOnly = false): Observable<InvoiceFee[]> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number | boolean> = { activeOnly };
    if (siteId != null) params['siteId'] = siteId;

    return this.apiMethodService.request<InvoiceFee[]>(
      '/InvoiceSettings/getInvoiceFeeList',
      'GET',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'getInvoiceFeeList', token, headerOnly: false, params }
    );
  }

  getInvoiceFeeById(id: number): Observable<InvoiceFee> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<InvoiceFee>(
      `/InvoiceSettings/getInvoiceFeeById/${id}`,
      'GET',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'getInvoiceFeeById', token, headerOnly: false }
    );
  }

  getInvoiceSiteConfig(siteId: number): Observable<InvoiceSiteConfig> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<InvoiceSiteConfig>(
      '/InvoiceSettings/getInvoiceSiteConfig',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getInvoiceSiteConfig',
        token,
        headerOnly: false,
        params: { siteId },
      }
    );
  }

  insertInvoiceFee(payload: InvoiceFeePayload): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/InvoiceSettings/insertInvoiceFee',
      'POST',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'insertInvoiceFee', token, headerOnly: false, body: payload }
    );
  }

  updateInvoiceFee(id: number, payload: InvoiceFeePayload): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      `/InvoiceSettings/updateInvoiceFee/${id}`,
      'PATCH',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'updateInvoiceFee', token, headerOnly: false, body: payload }
    );
  }

  deactivateInvoiceFee(id: number, payload: DeactivateInvoiceFeePayload): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      `/InvoiceSettings/deactivateInvoiceFee/${id}`,
      'PATCH',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'deactivateInvoiceFee', token, headerOnly: false, body: payload }
    );
  }

  upsertInvoiceSiteConfig(payload: InvoiceSiteConfigPayload): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/InvoiceSettings/upsertInvoiceSiteConfig',
      'POST',
      this.baseUrl,
      { isConsoleLog: true, labelLog: 'upsertInvoiceSiteConfig', token, headerOnly: false, body: payload }
    );
  }
}
